import { useLanguage } from '../../providers/preferenceProvider';
import '../../css/bookingListEntry.css';
import { useEffect, useState } from 'react';
import TimeRange from './timeRange';
import '../../css/timeline.css';
import { handleMove } from './util';
import { useConfig } from '../../providers/configProvider';
import '../../css/roomList.css';
import { ACL } from '../../acls';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCoins, faPeopleGroup } from '@fortawesome/free-solid-svg-icons';
import { faBuilding, faClock } from '@fortawesome/free-regular-svg-icons';

const RoomListEntry = ({ date, room, onBookingClick, onScroll, ref, availabilityDate, onRoomClick }) => {
  const { t } = useLanguage();
  const [selectedInterval, setSelectedInterval] = useState([]);
  const [disabledIntervals, setDisabledIntervals] = useState([]);
  const [validSelection, setValidSelection] = useState(true);
  const startTime = getTodayAtHour(0);
  const endTime = getTodayAtHour(24);
  const { isAuthorized } = useConfig();

  useEffect(() => {
    const now = new Date();
    const currentTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), now.getHours(), now.getMinutes(), 0, 0);

    let pastInterval = [];
    if (date.getDate() === now.getDate()) {
      pastInterval = [{
        start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
        end: currentTime,
        blocked: true
      }];
    }

    setDisabledIntervals([...pastInterval, ...room.Bookings.map(({ id, startTime, endTime, title, description, Users }) => {
      const start = new Date(startTime);
      const end = new Date(endTime);
      return {
        start,
        end,
        image: Users?.find(user => user?.UserBooking?.creator === true)?.image,
        Users: Users,
        onClick: (e) => {
          e.stopPropagation();
          onBookingClick(
            {
              id: id,
              room,
              startTime: start,
              endTime: end,
              title,
              Users,
              editable: false,
              credits: getCost(start, end),
              description: description,
              newBooking: false,
            }
          )
        },
      }
    }
    )]);

    const ms = currentTime.getTime();
    const interval = room.interval * 60 * 1000;
    const alignedStart = new Date(Math.ceil(ms / interval) * interval);
    const maxEnd = new Date(date);
    maxEnd.setHours(24, 0, 0, 0);


    const nextAvailable = findNextAvailableInterval(
      alignedStart,
      room.interval,
      [...pastInterval, ...room.Bookings.map(b => ({
        start: new Date(b.startTime),
        end: new Date(b.endTime)
      }))],
      maxEnd
    );
    if (!room.filtered) {
      setSelectedInterval(nextAvailable);

      if (nextAvailable.length === 2) {
        const [start, end] = nextAvailable;
        const overlaps = disabledIntervals.some(({ start: displayStart, end: displayEnd }) =>
          start < displayEnd && end > displayStart
        );
        setValidSelection(!overlaps);
      } else {
        setValidSelection(false);
      }
    }
  }, [room, date]);

  function findNextAvailableInterval(start, intervalMinutes, disabled, maxEndTime) {
    let currentStart = new Date(start);
    let currentEnd = new Date(currentStart.getTime() + intervalMinutes * 60 * 1000);
    while (currentEnd <= maxEndTime) {
      const overlaps = disabled.some(({ start: disableStart, end: disableEnd }) =>
        currentStart < disableEnd && currentEnd > disableStart
      );
      if (!overlaps) {
        return [currentStart, currentEnd];
      }

      currentStart = new Date(currentStart.getTime() + intervalMinutes * 60 * 1000);
      currentEnd = new Date(currentStart.getTime() + intervalMinutes * 60 * 1000);
    }

    return [];
  }

  useEffect(() => {
    if (availabilityDate) {
      const [start, end] = availabilityDate;
      const now = new Date();
      if (end > now) {
        setSelectedInterval(clampInterval(availabilityDate, room.maxBookingTime));
      }
    }
  }, [availabilityDate]);

  function clampInterval([start, end], maxMinutes) {
    const maxEndTime = new Date(start.getTime() + maxMinutes * 60 * 1000);
    const clampedEnd = end > maxEndTime ? maxEndTime : end;
    return [start, new Date(clampedEnd)];
  }

  function getBookingInfo() {
    return {
      room: room,
      startTime: selectedInterval[0],
      endTime: selectedInterval[1],
      editable: true,
      newBooking: true,
      credits: getCost(selectedInterval[0], selectedInterval[1]),
    }
  }

  function formatOpeningTimes(open, close) {
    if (open === '00:00:00' && close === '00:00:00')
      return 'All day'

    return `${formatTime(open)} - ${formatTime(close)}`;
  }

  function formatTime(timeString) {
    return timeString.substring(0, 5)
  }

  function onUpdate(event) {
    setValidSelection(!event.error);
    setSelectedInterval(event.time);
  }

  function onChange(event) {
    setSelectedInterval(event);
  }

  function getTodayAtHour(hour, minute) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute ?? 0, 0, 0);
  }

  function formatBookingDate(msDate) {
    const date = new Date(msDate);
    return `${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })}`;
  }

  function getCost(startTime, endTime) {
    if (!endTime || !startTime || !room.credits) return 0;
    const creditsPerMin = room.credits / room.interval;
    const bookingDurationInMins = Math.round(((endTime.getTime() - startTime.getTime()) / (1000 * 60)));
    return +(creditsPerMin * bookingDurationInMins).toFixed(2);
  }

  const scrollBy = (direction) => {
    if (ref?.current) {
      ref.current.scrollBy({ left: direction * 100, behavior: 'smooth' });
    }
  };

  return (
    <div className="room-list-entry-container bg-white drop-shadow-lg rounded-xl max-md:m-2">
      <div className="room-list-main md:w-max-full">
        <div className="room-picture-roomlist">
          <img src={room.image} alt="42Workspace" onClick={() => onRoomClick(room)} className={`object-cover cursor-pointer transition duration-200 ${room.filtered ? 'grayscale opacity-50' : ''}`} />
        </div>
        <div className="md:flex md:overflow-hidden md:flex-col ">
          <div className="room-list-availability availability-list md:flex md:flex-row gap-2 md:mt-2 max-md:ml-1">
            <div className="md:flex md:flex-col">
              <div className="md:whitespace-nowrap mb-2 text-xl  select-none">{room.name}</div>
              <div className="flex md:flex-row md:gap-2 max-md:gap-1 md:align-center max-md:mr-1">
                <div className="flex gap-2 text-sm whitespace-nowrap select-none border rounded px-1 py-0.5 border-black hover:text-gray-700" title='Opening Hours'>
                  <FontAwesomeIcon icon={faClock} />
                  <div className="text-xs">{formatOpeningTimes(room.openTime, room.closeTime)}</div>
                </div>

                <div className="flex gap-2 text-sm whitespace-nowrap select-none border rounded px-1 py-0.5 border-black hover:text-gray-700" title='Floor'>
                  <FontAwesomeIcon icon={faBuilding} />
                  <div className="text-xs">{room.floor}</div>
                </div>

                <div className="flex gap-2 text-sm whitespace-nowrap select-none border rounded px-1 py-0.5 border-black hover hover:text-gray-700" title='Capacity'>
                  <FontAwesomeIcon icon={faPeopleGroup} />
                  <div className="text-xs">{room.capacity}</div>
                </div>

                {room?.credits && isAuthorized(ACL.USE_CREDITS) ?
                  (room.credits * 60 / room.interval) > 1 ?
                    <div className="flex gap-2 text-sm whitespace-nowrap select-none border rounded px-1 py-0.5 border-black hover hover:text-gray-700" title='Credit Cost'>
                      <FontAwesomeIcon icon={faPeopleGroup} />
                      <div className="text-xs">{room.credits * 60 / room.interval}/hour</div>
                    </div> :
                    <div className="flex gap-2 text-sm whitespace-nowrap select-none border rounded px-1 py-0.5 border-black hover hover:text-gray-700" title='Credit Cost'>
                      <FontAwesomeIcon icon={faCoins} />
                      <div className="text-xs">{room.credits * 60 / room.interval}/hour</div>
                    </div>
                  : null}
                {/* </div> */}
              </div>
            </div>
          </div>


          <div className="room-list-timerange-scroll flex flex-row">
            <button onClick={() => scrollBy(-1.25)} className="md:hidden border border-gray-400 rounded mt-2 mb-2"><FontAwesomeIcon className='m-1' icon={faChevronLeft} size='xs'></FontAwesomeIcon></button>

            <div className="room-list-timerange-scroll rounded thin-scrollbar md:mt-1 md:mb-1" ref={ref} onScroll={onScroll}>
              <TimeRange
                containerClassName='h-[7rem] md:h-[4rem] min-w-[150rem]'
                error={!validSelection}
                ticksNumber={24 * (60 / room.interval)}
                step={3600000 / (60 / room.interval)}
                formatTick={ms => formatBookingDate(ms)}
                selectedInterval={selectedInterval}
                timelineInterval={[startTime, endTime]}
                onUpdateCallback={onUpdate}
                onChangeCallback={onChange}
                disabledIntervals={disabledIntervals}
                mode={(curr, next, step, reversed, getValue) => handleMove(curr, next, step, reversed, getValue, room.maxBookingTime * 60 * 1000)}
                trackStyles={{
                  error: {
                    backgroundColor: 'rgba(214,0,11,0.5)',
                    borderLeft: '1px solid rgba(214,0,11,0.5)',
                    borderRight: '1px solid rgba(214,0,11,0.5)',
                  },
                  valid: {
                    backgroundColor: 'rgba(98, 203, 102, 0.5)',
                    borderLeft: '1px solid #62CB66',
                    borderRight: '1px solid #62CB66',
                  }
                }}
                handleColors={{
                  error: 'rgb(214, 0, 11)',
                  valid: 'rgb(98, 203, 102)'
                }}
              />
            </div>

            <button onClick={() => scrollBy(1.25)} className="md:hidden border border-gray-400 rounded mt-2 mb-2"><FontAwesomeIcon className='m-1' icon={faChevronRight} size='xs'></FontAwesomeIcon></button>
          </div>
        </div>
        <div className="md:border-l-2 max-md:border-t-2 md:mr-2 border-[var(--white-color)]">
          <div className="room-list-align-center max-md:mb-1 md:ml-2 max-md:mt-1">
            <button
              disabled={!validSelection || room.filtered}
              className="admin-edit-button transition duration-200"
              onClick={() => onBookingClick(getBookingInfo())}
            >
              <div className='flex flex-col text-sm select-none'>
                {t.confirm}
                {isAuthorized(ACL.USE_CREDITS) ?
                  <>
                    <div className='flex flex-row justify-center items-center'>
                      <h4 className='text-xs mr-1'>{getCost(selectedInterval[0], selectedInterval[1])}</h4>
                      {getCost(selectedInterval[0], selectedInterval[1]) === 1 ? <h1 className='text-xs'>credit</h1> : <h1 className='text-xs'>credits</h1>}
                    </div>
                  </>
                  : null}
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomListEntry;
